import React, { useState, useEffect, useRef } from 'react';
import './ProductModal.css';
import PurchaseForm from '../PurchaseForm/PurchaseForm';
import { getImageUrl } from '../../../services/publicApi';

const ProductModal = ({ product, onClose }) => {
    const [currentImage, setCurrentImage] = useState(0);
    const [isClosing, setIsClosing] = useState(false);
    const [showPurchaseForm, setShowPurchaseForm] = useState(false);
    const [selectedSize, setSelectedSize] = useState('');
    const closingTimerRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const modalContentRef = useRef(null);
    const closeButtonRef = useRef(null);
    const previousFocusRef = useRef(null);

    const productName = product?.name || 'Без названия';
    const productPrice = product?.price || 0;
    const productImages = product?.images || [];
    const productDescription = product?.description || '';
    const variants = product?.variants || [];
    const availableVariants = variants.filter((variant) => variant.is_active && variant.available_quantity > 0);
    const selectedVariant = variants.find((variant) => variant.size === selectedSize);
    const isSoldOut = availableVariants.length === 0;

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        previousFocusRef.current = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onCloseRef.current?.();
                return;
            }

            if (event.key !== 'Tab' || !modalContentRef.current) return;

            const focusableElements = Array.from(modalContentRef.current.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            ));
            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const focusIsOutside = !modalContentRef.current.contains(document.activeElement);

            if (event.shiftKey && (document.activeElement === firstElement || focusIsOutside)) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && (document.activeElement === lastElement || focusIsOutside)) {
                event.preventDefault();
                firstElement.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        closeButtonRef.current?.focus();

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (closingTimerRef.current) clearTimeout(closingTimerRef.current);
            document.body.style.overflow = previousOverflow;
            previousFocusRef.current?.focus?.();
        };
    }, []);

    useEffect(() => {
        setSelectedSize('');
        setCurrentImage(0);
        setShowPurchaseForm(false);
        setIsClosing(false);
    }, [product?.id]);

    useEffect(() => {
        if (!showPurchaseForm) return undefined;

        const focusTimer = setTimeout(() => {
            modalContentRef.current?.querySelector('input:not([disabled]), textarea:not([disabled])')?.focus();
        }, 0);

        return () => clearTimeout(focusTimer);
    }, [showPurchaseForm]);

    const handleClose = () => {
        setIsClosing(true);
        if (closingTimerRef.current) clearTimeout(closingTimerRef.current);
        closingTimerRef.current = setTimeout(() => {
            onCloseRef.current?.();
        }, 300);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const nextImage = () => {
        if (productImages.length === 0) return;
        setCurrentImage((prev) => (prev + 1) % productImages.length);
    };

    const prevImage = () => {
        if (productImages.length === 0) return;
        setCurrentImage((prev) => (prev - 1 + productImages.length) % productImages.length);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('ru-RU').format(price) + ' ₽';
    };

    if (!product) return null;

    return (
        <div
            className={`shop-modal-overlay ${isClosing ? 'closing' : ''}`}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label={`${productName}. ${showPurchaseForm ? 'Оформление заказа' : 'Карточка товара'}`}
        >
            <div ref={modalContentRef} className={`shop-modal-content ${isClosing ? 'closing' : ''}`}>
                <button ref={closeButtonRef} className="shop-modal-close" onClick={handleClose} aria-label="Закрыть карточку товара">✕</button>

                {!showPurchaseForm ? (
                    <div className="shop-modal-body">
                        <div className="shop-modal-gallery">
                            <div className="shop-modal-main-image">
                                {productImages.length > 0 ? (
                                    <img
                                        src={getImageUrl(productImages[currentImage])}
                                        alt={productName}
                                        decoding="async"
                                    />
                                ) : (
                                    <div className="shop-no-image">Нет фото</div>
                                )}
                                {productImages.length > 1 && (
                                    <>
                                        <button className="shop-gallery-nav prev" onClick={prevImage} aria-label="Предыдущее фото">‹</button>
                                        <button className="shop-gallery-nav next" onClick={nextImage} aria-label="Следующее фото">›</button>
                                    </>
                                )}
                            </div>

                            {productImages.length > 1 && (
                                <div className="shop-modal-thumbnails">
                                    {productImages.map((img, index) => (
                                        <button
                                            type="button"
                                            key={index}
                                            className={`shop-modal-thumb ${index === currentImage ? 'active' : ''}`}
                                            onClick={() => setCurrentImage(index)}
                                            aria-label={`Показать фото ${index + 1}`}
                                            aria-pressed={index === currentImage}
                                        >
                                            <img
                                                src={getImageUrl(img)}
                                                alt={`${productName} - ${index + 1}`}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                            {productImages.length > 1 && (
                                <p className="shop-gallery-guidance">Спереди · сзади · деталь</p>
                            )}
                        </div>

                        <div className="shop-modal-info">
                            <div className="shop-modal-eyebrow">Это Россия · коллекция</div>
                            <h2 id="shop-product-title" className="shop-modal-title">{productName}</h2>
                            {product?.color_name && (
                                <div className="shop-modal-color">
                                    <i style={{ backgroundColor: product.color_code || '#777' }} aria-hidden="true" />
                                    <span>{product.color_name}</span>
                                </div>
                            )}
                            <div className="shop-modal-price">{formatPrice(productPrice)}</div>

                            {productDescription && (
                                <div className="shop-modal-description">
                                    <p>{productDescription}</p>
                                </div>
                            )}

                            {variants.length > 0 && (
                                <div className="shop-size-selection">
                                    <div className="shop-size-heading">
                                        <h3 id="shop-size-title">Выберите размер</h3>
                                        <span>Остаток указан рядом</span>
                                    </div>
                                    <div className="shop-size-options" role="group" aria-labelledby="shop-size-title">
                                        {variants.map((variant) => (
                                            <button
                                                key={variant.id}
                                                className={`shop-size-btn ${selectedSize === variant.size ? 'active' : ''}`}
                                                onClick={() => setSelectedSize(variant.size)}
                                                disabled={!variant.is_active || variant.available_quantity <= 0}
                                                aria-pressed={selectedSize === variant.size}
                                            >
                                                {variant.size}
                                                <small>{variant.available_quantity > 0 ? `${variant.available_quantity} шт.` : 'нет'}</small>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <button
                                className="shop-buy-btn"
                                onClick={() => setShowPurchaseForm(true)}
                                disabled={isSoldOut || !selectedVariant || selectedVariant.available_quantity <= 0}
                            >
                                {isSoldOut ? 'Нет в наличии' : (!selectedSize ? 'Сначала выберите размер' : 'Продолжить оформление')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <PurchaseForm
                        product={product}
                        variant={selectedVariant}
                        onBack={() => setShowPurchaseForm(false)}
                        onClose={handleClose}
                    />
                )}
            </div>
        </div>
    );
};

export default ProductModal;
